/**
 * Environment configuration step
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff, Key, AlertTriangle, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

type Props = {
  blueprintId?: string;
  onComplete: (config: Record<string, string>) => void;
  onBack: () => void;
};

type EnvVar = {
  key: string;
  label: string;
  description: string;
  required: boolean;
  secret: boolean;
  placeholder?: string;
};

const ENV_VARS: EnvVar[] = [
  {
    key: 'HF_TOKEN',
    label: 'Hugging Face Token',
    description: 'Required to download model weights',
    required: true,
    secret: true,
    placeholder: 'hf_...',
  },
  {
    key: 'OPENAI_API_KEY',
    label: 'OpenAI API Key (Optional)',
    description: 'For fallback or comparison',
    required: false,
    secret: true,
    placeholder: 'sk-...',
  },
  {
    key: 'MODEL_CACHE_DIR',
    label: 'Model Cache Directory',
    description: 'Where to store downloaded models',
    required: false,
    secret: false,
    placeholder: '/data/models',
  },
  {
    key: 'API_PORT',
    label: 'API Port',
    description: 'Port for API server',
    required: false,
    secret: false,
    placeholder: '8000',
  },
];

const IDRAC_ENV_VARS: EnvVar[] = [
  {
    key: 'IDRAC_ENDPOINT',
    label: 'iDRAC IP Address',
    description: 'Management interface IP (e.g., 192.168.1.100)',
    required: true,
    secret: false,
    placeholder: '192.168.1.100',
  },
  {
    key: 'IDRAC_USER',
    label: 'iDRAC Username',
    description: 'Administrative user for server management',
    required: true,
    secret: false,
    placeholder: 'root',
  },
  {
    key: 'IDRAC_PASSWORD',
    label: 'iDRAC Password',
    description: 'Server management credentials',
    required: true,
    secret: true,
    placeholder: '',
  },
];

export function EnvironmentStep({ blueprintId, onComplete, onBack }: Props) {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  const envVars = blueprintId === 'dell-inference-stack' ? IDRAC_ENV_VARS : ENV_VARS;
  const isDellStack = blueprintId === 'dell-inference-stack';

  const handleChange = (key: string, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const toggleSecret = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const generateEnvFile = () => {
    const entries = Object.entries(config)
      .filter(([_, value]) => value)
      .map(([key, value]) => {
        if (isDellStack) {
          // Terraform format
          return `${key.toLowerCase()} = "${value}"`;
        }
        // Standard .env format
        return `${key}="${value}"`;
      });
    return entries.join('\n');
  };

  const copyEnvFile = async () => {
    const content = generateEnvFile();
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Copied to Clipboard',
        description: 'Environment variables copied',
      });
    } catch (err) {
      toast({
        title: 'Copy Failed',
        description: 'Could not copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = () => {
    const requiredMissing = envVars.filter(
      v => v.required && !config[v.key]
    );

    if (requiredMissing.length > 0) {
      toast({
        title: 'Missing Required Fields',
        description: `Please provide: ${requiredMissing.map(v => v.label).join(', ')}`,
        variant: 'destructive',
      });
      return;
    }

    onComplete(config);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {isDellStack ? 'iDRAC Configuration' : 'Environment Configuration'}
          </CardTitle>
          <CardDescription>
            {isDellStack 
              ? 'Provide Dell iDRAC credentials for out-of-band server management'
              : 'Configure environment variables and credentials for your deployment'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {envVars.map((envVar) => (
            <div key={envVar.key} className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor={envVar.key} className="flex items-center gap-2">
                  {envVar.secret && <Key className="h-4 w-4 text-muted-foreground" />}
                  {envVar.label}
                  {envVar.required && (
                    <Badge variant="destructive" className="text-xs">
                      Required
                    </Badge>
                  )}
                </Label>
                {envVar.secret && config[envVar.key] && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSecret(envVar.key)}
                  >
                    {showSecrets[envVar.key] ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
              <Input
                id={envVar.key}
                type={envVar.secret && !showSecrets[envVar.key] ? 'password' : 'text'}
                placeholder={envVar.placeholder}
                value={config[envVar.key] || ''}
                onChange={(e) => handleChange(envVar.key, e.target.value)}
                className={envVar.required && !config[envVar.key] ? 'border-red-300' : ''}
              />
              <p className="text-xs text-muted-foreground">{envVar.description}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {Object.keys(config).length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {isDellStack ? 'terraform.tfvars Preview' : '.env File Preview'}
              </CardTitle>
              <Button variant="outline" size="sm" onClick={copyEnvFile}>
                {copied ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="p-4 rounded-md bg-muted text-xs font-mono overflow-x-auto">
              {generateEnvFile()}
            </pre>
          </CardContent>
        </Card>
      )}

      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Keep your credentials secure. Never commit .env files to version control.
        </AlertDescription>
      </Alert>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleSubmit}>
          Continue
        </Button>
      </div>
    </div>
  );
}
